import { useApi } from 'api'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router'
import { ITEMS_PER_PAGE } from './constants'
import { tableColumns } from './structure'
import { Button, Card, Flex, Skeleton, Table } from 'antd'
import Filters from './Filters'

const InvoicesList = (): React.ReactElement => {
  const api = useApi()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentPage = Number(searchParams.get('page')) || 1
  const filters = searchParams.get('filters') || ''

  const navigate = useNavigate()

  const { data, isLoading, isFetched, isFetching } = useQuery({
    queryKey: ['invoices', currentPage, ITEMS_PER_PAGE, filters],
    queryFn: () =>
      api.getInvoices({
        page: currentPage,
        per_page: ITEMS_PER_PAGE,
        filter: filters
      }),
    select: (responseData) => responseData.data,
    placeholderData: keepPreviousData,
  })

  const handlePageChange = (page: number) => {
    searchParams.set('page', page.toString())
    setSearchParams(searchParams)
  }

  const fetchCompleted = !isLoading && !isFetching && isFetched

  return (
    <div>
      <Button type="primary" href="/invoices/new">
        Create Invoice
      </Button>
      <Card title="Filters" size="small" className='my-4'>
        {fetchCompleted && (<Filters 
          params={searchParams} 
          setParamsCallback={setSearchParams} 
          invoices={data?.invoices || []}
        />)}
        {!fetchCompleted && (
          <>
            <Flex gap="1rem" wrap="nowrap">
              <Skeleton.Input active block />
              <Skeleton.Input active block />
              <Skeleton.Input active block />
            </Flex>
            <Skeleton.Button active className='my-2' />
          </>
        )}
      </Card>
      <Table
        dataSource={data?.invoices ?? []}
        columns={tableColumns}
        rowKey="id"
        pagination={
          data?.pagination && {
            total: data?.pagination?.total_entries ?? 0,
            pageSize: ITEMS_PER_PAGE,
            onChange: (page) => handlePageChange(page),
            current: currentPage,
            showSizeChanger: false,
          }
        }
        onRow={(record) => ({
          onClick: (e) => {
            if (e.metaKey || e.ctrlKey) {
              const win = window.open(`/invoices/${record.id}`, '_blank')
              win?.focus()
            } else {
              navigate(`/invoices/${record.id}`)
            }
          },
          role: 'button',
        })}
      />
    </div>
  )
}

export default InvoicesList
